import React, { useMemo, useState, useEffect, useRef } from 'react';
import { renderIcon, DefaultCampaignIcon } from '../../utils/renderIcon';
import { buildDependencyGraph } from '../../layout';
import { findTopLevelMissions } from '../../layout/topLevelOrigins';
import { useLabelPositioning } from './useLabelPositioning';
import { DEBUG_VISUAL_OVERLAY } from './utils/constants';
import './styles.css';

// Default node dimensions (should match TaskNode)
const DEFAULT_NODE_WIDTH = 288;
const DEFAULT_NODE_HEIGHT = 162;

// Padding around each campaign zone
const ZONE_PADDING = 50;

// Extra top padding when chain headers are present
const CHAIN_HEADER_HEIGHT = 70;

// Duration for fade transition when changing edges (ms)
const EDGE_FADE_DURATION = 100;

/**
 * Debug overlay component
 */
const DebugOverlay = ({ labelPos, zoneBounds }) => {
  if (!DEBUG_VISUAL_OVERLAY || !labelPos) return null;
  
  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: labelPos.centerX - zoneBounds.x - 5,
          top: labelPos.centerY - zoneBounds.y - 5,
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: 'cyan',
          border: '2px solid lime',
          pointerEvents: 'none',
          zIndex: 10000,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: labelPos.centerX - zoneBounds.x + 10,
          top: labelPos.centerY - zoneBounds.y - 10,
          fontSize: '10px',
          color: 'lime',
          background: 'rgba(0,0,0,0.8)',
          padding: '2px 4px',
          borderRadius: '2px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      >
        {labelPos.edge} | {labelPos.isVisible ? 'visible' : 'hidden'}
      </div>
    </>
  );
};

/**
 * Campaign label with edge-change fade transition
 * Uses refs to always have access to latest labelPos when fade completes
 */
const CampaignLabel = ({ labelPos, zoneBounds, campaign, onCampaignClick, onRemoveCampaign, isRemoving = false }) => {
  const [isFading, setIsFading] = useState(false);
  const [displayPos, setDisplayPos] = useState(null);
  const prevEdgeRef = useRef(null);
  const timeoutRef = useRef(null);
  // Keep latest labelPos in a ref so timeout callback always has current value
  const latestLabelPosRef = useRef(labelPos);
  
  // Always update the ref with latest labelPos
  useEffect(() => {
    latestLabelPosRef.current = labelPos;
  }, [labelPos]);
  
  useEffect(() => {
    // Clear any pending timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  
  useEffect(() => {
    // If no labelPos, clear display
    if (!labelPos) {
      setDisplayPos(null);
      prevEdgeRef.current = null;
      return;
    }
    
    const prevEdge = prevEdgeRef.current;
    const currentEdge = labelPos.edge;
    
    // Check if edge changed (and we had a previous edge)
    const edgeChanged = prevEdge !== null && prevEdge !== currentEdge;
    
    if (edgeChanged && !isFading) {
      // Start fade out
      setIsFading(true);
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // After fade out, use the LATEST labelPos from ref (not the stale closure value)
      timeoutRef.current = setTimeout(() => {
        const latest = latestLabelPosRef.current;
        if (latest) {
          setDisplayPos(latest);
          prevEdgeRef.current = latest.edge;
        }
        setIsFading(false);
        timeoutRef.current = null;
      }, EDGE_FADE_DURATION);
    } else if (!isFading) {
      // Same edge or first render - update immediately
      setDisplayPos(labelPos);
      prevEdgeRef.current = currentEdge;
    }
    // If currently fading, don't update displayPos - let the timeout handle it
    // But the ref is always updated, so timeout will get the latest position
  }, [labelPos, isFading]);
  
  // Nothing to render
  if (!displayPos) return null;
  
  const isVertical = displayPos.edge === 'left' || displayPos.edge === 'right';
  
  const classNames = [
    'campaign-zone__label',
    'campaign-zone__label--positioned',
    displayPos.isVisible === false && 'campaign-zone__label--hidden',
    isVertical && 'campaign-zone__label--vertical',
    isFading && 'campaign-zone__label--fading',
    isRemoving && 'campaign-zone__label--removing',
  ].filter(Boolean).join(' ');
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (onCampaignClick && campaign) {
      onCampaignClick(campaign);
    }
  };
  
  const handleRemove = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRemoveCampaign && campaign) {
      onRemoveCampaign(campaign.id);
    }
  };
  
  return (
    <div 
      className={classNames}
      style={{
        left: displayPos.centerX - zoneBounds.x,
        top: displayPos.centerY - zoneBounds.y,
      }}
      onClick={handleClick}
    >
      {isRemoving ? (
        <>
          <span className="campaign-zone__spinner" />
          <span className="campaign-zone__name">Removing...</span>
        </>
      ) : (
        <>
          <span className="campaign-zone__icon">
            {renderIcon(campaign.icon, { size: '14px', fallback: <DefaultCampaignIcon size={14} /> })}
          </span>
          <span className="campaign-zone__name">{campaign.name}</span>
          {onRemoveCampaign && (
            <button 
              type="button"
              className="campaign-zone__remove"
              onClick={handleRemove}
              onMouseDown={(e) => e.stopPropagation()}
              title="Remove from view"
            >
              ×
            </button>
          )}
        </>
      )}
    </div>
  );
};

/**
 * CampaignZones Component
 */
const CampaignZones = ({ 
  tasks = [],
  campaigns = [],
  positions = {},
  nodeDimensions = {},
  isAnimating = false,
  containerRef,
  pan = { x: 0, y: 0 },
  zoom = 1,
  onCampaignClick,
  onRemoveCampaign,
  removingCampaignId = null,
}) => {
  const campaignZones = useMemo(() => {
    if (campaigns.length === 0 || tasks.length === 0) return [];

    const campaignMap = new Map(campaigns.map(c => [c.id, c]));

    const tasksByCampaign = {};
    tasks.forEach(task => {
      if (task.sourceCampaign?.id) {
        const campaignId = task.sourceCampaign.id;
        if (!tasksByCampaign[campaignId]) {
          tasksByCampaign[campaignId] = [];
        }
        tasksByCampaign[campaignId].push(task);
      }
    });

    return Object.entries(tasksByCampaign).map(([campaignId, campaignTasks]) => {
      const campaign = campaignMap.get(campaignId) || campaignTasks[0]?.sourceCampaign;
      if (!campaign) return null;

      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;

      campaignTasks.forEach(task => {
        const pos = positions[task.id] || task.position || { x: 0, y: 0 };
        const dims = nodeDimensions[task.id] || { 
          width: DEFAULT_NODE_WIDTH, 
          height: DEFAULT_NODE_HEIGHT 
        };

        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x + dims.width);
        maxY = Math.max(maxY, pos.y + dims.height);
      });

      if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
        return null;
      }

      // Every top-level mission draws a flag, including a campaign that is one chain.
      const hasChainHeaders = findTopLevelMissions(
        campaignTasks,
        buildDependencyGraph(campaignTasks),
      ).length > 0;
      const topPadding = hasChainHeaders ? ZONE_PADDING + CHAIN_HEADER_HEIGHT : ZONE_PADDING;

      return {
        id: campaignId,
        campaign,
        bounds: {
          x: minX - ZONE_PADDING,
          y: minY - topPadding,
          width: maxX - minX + (ZONE_PADDING * 2),
          height: maxY - minY + ZONE_PADDING + topPadding,
        },
        taskCount: campaignTasks.length,
      };
    }).filter(Boolean);
  }, [tasks, campaigns, positions, nodeDimensions]);

  const labelPositions = useLabelPositioning({
    campaignZones,
    containerRef,
    pan,
    zoom,
    isAnimating, // Skip calculations during animation to prevent stutter
  });

  if (campaignZones.length === 0) {
    return null;
  }

  const containerClassName = `campaign-zones${isAnimating ? ' campaign-zones--animating' : ''}`;

  return (
    <div className={containerClassName}>
      {campaignZones.map(zone => (
        <div
          key={zone.id}
          className="campaign-zone"
          style={{
            left: zone.bounds.x,
            top: zone.bounds.y,
            width: zone.bounds.width,
            height: zone.bounds.height,
            '--zone-color': zone.campaign.color,
          }}
        >
          {/* Skip rendering labels entirely during animation to prevent stutter */}
          {!isAnimating && (
            <>
              <DebugOverlay labelPos={labelPositions[zone.id]} zoneBounds={zone.bounds} />
              <CampaignLabel 
                labelPos={labelPositions[zone.id]} 
                zoneBounds={zone.bounds}
                campaign={zone.campaign}
                onCampaignClick={onCampaignClick}
                onRemoveCampaign={onRemoveCampaign}
                isRemoving={removingCampaignId === zone.campaign.id}
              />
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default CampaignZones;
